/* import __COLOCATED_TEMPLATE__ from './settings.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type Fin from 'embercom/models/operator/visual-builder/step/fin';
import { normalizeSelectedAgentForStandalone } from 'embercom/models/operator/visual-builder/step/fin';

import {
  type AllChannels as Channel,
  EXTERNAL_TICKET_CHANNELS,
} from 'embercom/lib/operator/custom-bots/constants';
import {
  FIN_CSAT_COMPATIBLE_CHANNELS,
  AGENT_OPTIONS,
} from 'embercom/lib/operator/resolution-bot/constants';
import type { AgentTypeValue } from 'embercom/lib/ai-agent/agent-type-config';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import type FinConfiguration from 'embercom/objects/visual-builder/configuration/step/fin';
import { State } from 'embercom/models/data/matching-system/matching-constants';
import type FinAgentTypeService from 'embercom/services/fin-agent-type-service';

interface Arguments {
  step: Fin;
  isReadOnly: boolean;
  customAnswerCount: number;
  triageButtonLabel: string;
  thatHelpedButtonLabel: string;
  canUseAiAnswers: boolean;
  usesResolutionsWithCustomAnswers: boolean;
  canUseCustomAnswers: boolean;
  editorState: EditorState;
  stepConfig: FinConfiguration;
  guidelinesCount?: number;
  categoriesCount?: number;
  hasShopifyConnected: boolean;
  hasNoEcommerceProducts: boolean;
  isMessengerInstalledForWeb: boolean;
  onSelectAgent: (agentValue: string) => void;
  sdrEscalationMode?: string | null;
}

export default class Settings extends Component<Arguments> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare contentEditorService: any;
  @service declare appService: AppService;
  @service declare attributeService: any;
  @service declare finAgentTypeService: FinAgentTypeService;
  @tracked openSectionId = '';
  @tracked setupAccordionOpenSectionId = '';
  @tracked sdrFlowAccordionOpenSectionId = '';
  @tracked ecommerceFlowAccordionOpenSectionId = '';
  @tracked isUsageDrawerOpen = false;

  constructor(owner: unknown, args: Arguments) {
    super(owner, args);
    let normalized = normalizeSelectedAgentForStandalone(
      this.args.step.selectedAgentValue,
      this.appService.app,
    );
    if (normalized !== this.args.step.selectedAgentValue) {
      this.args.step.selectedAgent = normalized;
    }
  }

  @action
  openAndScrollToAccordionSection(sectionId: string) {
    // Set the accordion section to open
    this.openSectionId = sectionId;

    // Scroll to the accordion section after allowing it to open
    requestAnimationFrame(() => {
      let targetElement = document.querySelector(`[data-accordion-section="${sectionId}"]`);
      targetElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    });
  }

  @tracked showCsatBlockAfterUpdate =
    this.args.step.csatBlockUpdateAfter !== undefined &&
    this.args.step.csatBlockUpdateAfter !== null;

  get appId() {
    return this.appService.app.id;
  }

  get oneTimeModeAvailable() {
    return (
      this.args.step.useCustomAnswers &&
      !this.args.step.useAiAnswers &&
      this.args.step.botMode !== 'bot_only'
    );
  }

  get botOnlyModeAvailable() {
    return this.args.step.botMode !== 'one_time';
  }

  get shouldShowChannelTypeHeaders() {
    if (!this.args.stepConfig) {
      return true;
    }

    return this.args.stepConfig?.showChannelTypeHeaders;
  }

  get shouldShowPreHandoverSettings() {
    if (!this.args.stepConfig) {
      return true;
    }

    return this.args.stepConfig?.showFinPreHandoverSection;
  }

  get isFinService() {
    return this.args.step.selectedAgentValue === AGENT_OPTIONS.FIN;
  }

  get isFinSdr() {
    return this.args.step.selectedAgentValue === AGENT_OPTIONS.FIN_SDR;
  }

  get isFinEcommerce() {
    return this.args.step.selectedAgentValue === AGENT_OPTIONS.FIN_ECOMMERCE;
  }

  get isFinSuccess() {
    return this.args.step.selectedAgentValue === AGENT_OPTIONS.FIN_SUCCESS;
  }

  get isFinProactive() {
    return this.args.step.selectedAgentValue === AGENT_OPTIONS.FIN_PROACTIVE;
  }

  get isChatWorkflow() {
    let channels = this.contentEditorService.activeObject?.targetChannels || [];
    return channels.length === 0 || channels.includes('web');
  }

  get shouldShowCsatSettings() {
    if (!this.args.stepConfig) {
      return true;
    }

    return this.args.stepConfig?.showFinCsatSection;
  }

  get shouldShowAutoCloseSettings() {
    if (!this.args.stepConfig) {
      return true;
    }

    return this.args.stepConfig?.showFinAutoCloseSection;
  }

  get shouldShowRelocatedSettingsMessage() {
    return (
      this.isFinSdr &&
      !this.shouldShowAutoCloseSettings &&
      this.appService.app?.canUseFeature('sdr-lead-recovery-relocated-settings-msg')
    );
  }

  get shouldShowFollowUpSettings() {
    if (
      !this.appService.app?.canUseFeature('answerbot-conversational-fin') ||
      (!this.appService.app?.canUseFeature('answerbot-conversational-opt-in') &&
        this.appService.app?.canUseFeature('answerbot-conversational-fin-opt-out'))
    ) {
      return false;
    }
    if (!this.args.stepConfig) {
      return true;
    }
    return (
      this.args.stepConfig?.showFollowUpSettings &&
      this.args.stepConfig?.showFinSetExpectationsSection
    );
  }

  get shouldShowMessengerSettings() {
    if (!this.args.stepConfig) {
      return false;
    }
    return this.args.stepConfig?.showMessengerSettings;
  }

  get shouldShowConfirmResolutionSettings() {
    if (!this.args.stepConfig) {
      return false;
    }
    return this.args.stepConfig?.showConfirmResolutionSettings;
  }

  get shouldShowEmailFollowUpSettings() {
    if (this.isFinSdr) {
      return false;
    }
    let workflow = this.args.editorState?.workflow;
    if (workflow?.workflowInstanceTrigger === 'no-trigger') {
      return true;
    }
    return this.targetingEmail;
  }

  get shouldShowMultiParticipantSettings() {
    if (!this.appService.app?.canUseFeature('fin-email-multi-participant-settings')) {
      return false;
    }
    if (this.isFinSdr) {
      return false;
    }
    let workflow = this.args.editorState?.workflow;
    if (workflow?.workflowInstanceTrigger === 'no-trigger') {
      return true;
    }
    return this.targetingEmail;
  }

  get shouldShowSetExpectationsSettings() {
    if (!this.args.stepConfig) {
      return true;
    }
    return this.args.stepConfig?.showFinSetExpectationsSection;
  }

  get shouldShowHandoffToExternalTicketOnResolutionSection() {
    let isZendesk = this.appService.app.hasStandalonePlatform('zendesk');
    let isSalesforce = this.appService.app.hasStandalonePlatform('salesforce');
    let isZendeskWithFlag =
      isZendesk &&
      this.appService.app.canUseFeature('team-standalone-zendesk-ticket-handoff-on-resolution');
    if (this.args.stepConfig && (isSalesforce || isZendeskWithFlag)) {
      return this.args.stepConfig?.showHandoffToExternalTicketOnResolutionSection;
    }
    return false;
  }

  get shouldShowHardResolutionWorkflowSettings() {
    if (!this.args.stepConfig) {
      return false;
    }
    return this.args.stepConfig?.showHardResolutionWorkflowSettings;
  }

  get shouldShowUsageManagement() {
    if (!this.args.stepConfig) {
      return false;
    }
    return this.args.stepConfig?.showUsageManagement;
  }

  get shouldShowAllChannelsHeader() {
    return (
      this.shouldShowChannelTypeHeaders &&
      (this.shouldShowCsatSettings ||
        this.shouldShowAutoCloseSettings ||
        this.shouldShowHardResolutionWorkflowSettings ||
        this.shouldShowUsageManagement)
    );
  }

  get channelIncompatibleWithFinCSAT() {
    let channels = this.contentEditorService.activeObject?.targetChannels || [];
    if (!channels.length) {
      return false;
    }

    let incompatibleChannels = channels.filter((channel: Channel) => {
      return !FIN_CSAT_COMPATIBLE_CHANNELS.includes(channel);
    });
    return incompatibleChannels.length;
  }

  get emailLikeChannel() {
    let channels = this.contentEditorService.activeObject?.targetChannels || [];
    return channels.find(
      (channel: Channel) => channel === 'email' || EXTERNAL_TICKET_CHANNELS.includes(channel),
    );
  }

  get targetingEmail() {
    let channels = this.contentEditorService.activeObject?.targetChannels || [];
    if (!channels.length) {
      return false;
    }
    return channels.some(
      (channel: Channel) => channel === 'email' || EXTERNAL_TICKET_CHANNELS.includes(channel),
    );
  }

  get answerTypeSummary() {
    if (this.args.step.useCustomAnswers) {
      if (this.args.step.useAiAnswers) {
        return this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.control-answer-type.ai-and-custom-answers',
        );
      } else {
        return this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.control-answer-type.custom-answers',
        );
      }
    }

    return this.intl.t(
      'operator.workflows.visual-builder.pass-to-fin.settings.control-answer-type.ai-answers',
    );
  }

  get previewConfiguration() {
    return this.store.peekRecord('messenger-settings/all', this.appService.app.id)
      ?.previewConfiguration;
  }

  get shouldShowAgentPicker() {
    if (this.isFinProactive) {
      return false;
    }
    return this.agentOptions.length > 1;
  }

  get shouldUseAgentGridLayout() {
    return this.agentOptions.length >= 4;
  }

  get agentOptions() {
    let isStandalone = this.appService.app?.canUseStandalone;
    let options: { text: string; value: AgentTypeValue }[] = [
      {
        text: this.intl.t('operator.workflows.visual-builder.fin.agents.customer-support'),
        value: AGENT_OPTIONS.FIN,
      },
    ];

    let canShowSalesAgent = isStandalone
      ? this.appService.app?.canUseFeature('ca-fin-sales-standalone')
      : this.appService.app?.canUseFinSalesAgent;

    if (canShowSalesAgent) {
      options.push({
        text: this.intl.t('operator.workflows.visual-builder.fin.agents.sales-development'),
        value: AGENT_OPTIONS.FIN_SDR,
      });
    }

    if (!isStandalone && this.appService.app?.canUseFeature('fin-ecommerce-agent')) {
      options.push({
        text: this.intl.t('operator.workflows.visual-builder.fin.agents.fin-ecommerce'),
        value: AGENT_OPTIONS.FIN_ECOMMERCE,
      });
    }

    if (this.appService.app?.canUseFeature('fin-success-agent')) {
      options.push({
        text: this.intl.t('operator.workflows.visual-builder.fin.agents.customer-success'),
        value: AGENT_OPTIONS.FIN_SUCCESS,
      });
    }

    return options;
  }

  get defaultAgent() {
    switch (this.finAgentTypeService.currentAgentType) {
      case 'ecommerce':
        return AGENT_OPTIONS.FIN_ECOMMERCE;
      case 'sales':
        return AGENT_OPTIONS.FIN_SDR;
      case 'success':
        return AGENT_OPTIONS.FIN_SUCCESS;
      default:
        return AGENT_OPTIONS.FIN;
    }
  }

  get liveGuidelines() {
    return this.store
      .peekAll('ai-agent/guidelines')
      .rejectBy('isDeleted')
      .filterBy('state', State.Live)
      .toArray();
  }

  get liveGuidelinesCount() {
    return this.liveGuidelines.length;
  }

  get categoriesCount() {
    return this.attributeService.aiCategoriesDescriptors.filterBy('isEnabled', true).length;
  }

  get escalationGuidanceCount() {
    return this.liveGuidelines.filter((guideline) => guideline.category === 'routing').length;
  }

  get escalationRulesCount() {
    return this.store.peekAll('ai-agent/escalation-rule').filterBy('enabled', true).length;
  }

  @action
  toggleAiAnswers() {
    this.args.step.useAiAnswers = !this.args.step.useAiAnswers;
    if (this.args.step.useAiAnswers && this.args.step.botMode !== 'bot_only') {
      this.args.step.botMode = 'looping';
    } else if (!this.args.step.useAiAnswers) {
      this.args.step.preHandoverAnswerEnabled = false;
      if (!this.args.step.useCustomAnswers) {
        this.args.step.useCustomAnswers = true;
      }
    }
  }

  @action
  toggleCustomAnswers() {
    this.args.step.useCustomAnswers = !this.args.step.useCustomAnswers;
    if (this.args.step.useCustomAnswers && this.args.step.botMode !== 'bot_only') {
      this.args.step.botMode = 'looping';
    } else if (!this.args.step.useAiAnswers && !this.args.step.useCustomAnswers) {
      this.args.step.useAiAnswers = true;
      this.args.step.botMode = 'looping';
    }
  }

  @action
  toggleOneTimeMode() {
    this.args.step.botMode = this.args.step.botMode === 'one_time' ? 'looping' : 'one_time';
    if (this.args.step.botMode === 'one_time') {
      this.args.step.preHandoverAnswerEnabled = false;
    }
  }

  @action
  toggleBotOnlyMode() {
    this.args.step.botMode = this.args.step.botMode === 'bot_only' ? 'looping' : 'bot_only';
    if (this.args.step.botMode === 'bot_only') {
      this.args.step.preHandoverAnswerEnabled = false;
    }
  }

  @action
  toggleCsatEnabledForHardResolution() {
    this.args.step.csatEnabledForHardResolution = !this.args.step.csatEnabledForHardResolution;

    this.settleCsatBlockAfterUpdate();
  }

  @action
  toggleCsatEnabledForSoftResolution() {
    this.args.step.csatEnabledForSoftResolution = !this.args.step.csatEnabledForSoftResolution;

    this.settleCsatBlockAfterUpdate();
  }

  @action
  updateCsatCustomMessage(content: string) {
    this.args.step.csatCustomMessage = content;

    this.settleCsatBlockAfterUpdate();
  }

  @action
  toggleUseCsatCustomMessage() {
    this.args.step.useCsatCustomMessage = !this.args.step.useCsatCustomMessage;

    this.settleCsatBlockAfterUpdate();
  }

  @action
  toggleConfirmResolution() {
    this.args.step.confirmResolutionEnabled = !this.args.step.confirmResolutionEnabled;
  }

  settleCsatBlockAfterUpdate() {
    if (this.args.step.isAnyCSATSettingEnabled) {
      return;
    }

    this.args.step.csatBlockUpdateAfter = undefined;
    this.showCsatBlockAfterUpdate = false;
  }

  @action
  toggleRatingChange() {
    this.showCsatBlockAfterUpdate = !this.showCsatBlockAfterUpdate;

    if (!this.showCsatBlockAfterUpdate) {
      this.args.step.csatBlockUpdateAfter = undefined;
    } else if (
      this.args.step.csatBlockUpdateAfter === undefined ||
      this.args.step.csatBlockUpdateAfter === null
    ) {
      this.args.step.csatBlockUpdateAfter = 0;
    }
  }

  @action
  setBlockUpdateAfter(e: InputEvent) {
    let value = parseInt((e.target as HTMLInputElement).value, 10);
    if (isNaN(value)) {
      value = 0;
    }
    this.args.step.csatBlockUpdateAfter = value * 3600;
  }

  @action
  openUsageDrawer() {
    this.isUsageDrawerOpen = true;
  }

  @action
  onUsageDrawerOpenChange(isOpen: boolean) {
    this.isUsageDrawerOpen = isOpen;
  }

  @action
  selectAgent(agentValue: string) {
    this.args.onSelectAgent(agentValue);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin::Settings': typeof Settings;
    'workflows/graph-editor/node-items/steps/fin/settings': typeof Settings;
  }
}
